import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Debounce the "currently hovered car" used to drive the map marker swap.
 *
 * Dragging the mouse quickly across many result cards would otherwise thrash
 * the matching map marker on every `mouseenter`/`mouseleave`. The raw hovered
 * id is tracked synchronously in a ref (preserving the "only clear if it's
 * still me" guard) and only committed to React state after a short settle, so
 * a fast sweep commits a single final value rather than one update per card.
 *
 * Camera stillness on hover is guaranteed independently by the map decoupling
 * selection from `showItems()`; this hook is marker-thrash polish.
 */
export function useHoverDebounce(
  delayMs = 100,
): readonly [number | null, (carId: number, isHovered: boolean) => void] {
  const [hoveredCarId, setHoveredCarId] = useState<number | null>(null);
  const rawHoveredCarIdRef = useRef<number | null>(null);
  const hoverCommitTimerRef = useRef<number | null>(null);

  const handleCardHover = useCallback(
    (carId: number, isHovered: boolean) => {
      if (isHovered) rawHoveredCarIdRef.current = carId;
      else if (rawHoveredCarIdRef.current === carId) rawHoveredCarIdRef.current = null;
      if (hoverCommitTimerRef.current != null) window.clearTimeout(hoverCommitTimerRef.current);
      hoverCommitTimerRef.current = window.setTimeout(() => {
        setHoveredCarId(rawHoveredCarIdRef.current);
      }, delayMs);
    },
    [delayMs],
  );

  useEffect(
    () => () => {
      if (hoverCommitTimerRef.current != null) window.clearTimeout(hoverCommitTimerRef.current);
    },
    [],
  );

  return [hoveredCarId, handleCardHover] as const;
}
